// The Modules page's requests to the registry (web/server/lib/registry.mjs),
// with the browser's own session cookie, and its passkey ceremonies.

export class ApiError extends Error {
  constructor(status, message, body = {}) {
    super(message);
    this.status = status;
    this.body = body;
  }
}

const b64url = {
  encode(buffer) {
    const bytes = new Uint8Array(buffer);
    let binary = "";
    for (let k = 0; k < bytes.length; k += 0x8000) binary += String.fromCharCode(...bytes.subarray(k, k + 0x8000));
    return btoa(binary).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
  },
  decode(text) {
    const binary = atob(text.replace(/-/g, "+").replace(/_/g, "/"));
    const bytes = new Uint8Array(binary.length);
    for (let k = 0; k < binary.length; ++k) bytes[k] = binary.charCodeAt(k);
    return bytes.buffer;
  },
};
export { b64url };

const enc = encodeURIComponent;

export class Api {
  constructor(base = new URL(globalThis.b3dNative ? "../api/" : "/api/", location.href)) {
    this.base = base;
  }

  async request(method, path, { json, body, raw } = {}) {
    const headers = {};
    if (method !== "GET") headers["X-B3D-IDE"] = "1";
    if (json !== undefined) headers["Content-Type"] = "application/json";
    let response;
    try {
      response = await fetch(new URL(path, this.base), { method, headers, credentials: "same-origin",
        body: json !== undefined ? JSON.stringify(json) : body });
    } catch {
      throw new ApiError(0, "The server can't be reached. Check the connection, then try again");
    }
    if (raw && response.ok) return response;
    const result = (response.headers.get("content-type") || "").includes("json") ? await response.json() : {};
    if (!response.ok) throw new ApiError(response.status, result.error || `The server answered ${response.status}`, result);
    return result;
  }

  get(path) { return this.request("GET", path); }
  post(path, json = {}) { return this.request("POST", path, { json }); }

  config() { return this.get("config"); }
  me() { return this.get("me"); }
  setName(name) { return this.request("PUT", "me/name", { json: { name } }); }
  catalogue({ q = "", sort = "popular", platform = "", channel = "", page = 0 } = {}) {
    const query = new URLSearchParams({ sort, page: String(page) });
    if (q) query.set("q", q);
    if (platform) query.set("platform", platform);
    if (channel) query.set("channel", channel);
    return this.get(`modules?${query}`);
  }
  module(name) { return this.get(`modules/${enc(name)}`); }
  version(name, version) { return this.get(`modules/${enc(name)}/versions/${enc(version)}`); }
  async file(name, version, hash) {
    const response = await this.request("GET", `modules/${enc(name)}/versions/${enc(version)}/files/${hash}`, { raw: true });
    return new Uint8Array(await response.arrayBuffer());
  }
  use(name, version) { return this.post(`modules/${enc(name)}/uses`, { version }); }
  promote(name) { return this.post(`modules/${enc(name)}/promote`); }
  lookup(modules) { return this.post("modules/lookup", { modules }); }
  createModule(fields) { return this.post("modules", fields); }
  updateModule(name, fields) { return this.request("PATCH", `modules/${enc(name)}`, { json: fields }); }
  deleteModule(name) { return this.request("DELETE", `modules/${enc(name)}`); }
  createVersion(name, body) { return this.post(`modules/${enc(name)}/versions`, body); }
  deleteVersion(name, version) { return this.request("DELETE", `modules/${enc(name)}/versions/${enc(version)}`); }
  withdraw(name, version, reason) { return this.post(`modules/${enc(name)}/versions/${enc(version)}/withdraw`, { reason }); }
  rate(name, stars) { return this.request("PUT", `modules/${enc(name)}/rating`, { json: { stars } }); }
  unrate(name) { return this.request("DELETE", `modules/${enc(name)}/rating`); }
  comments(name, page = 0) { return this.get(`modules/${enc(name)}/comments?page=${page}`); }
  comment(name, text, parent = null) { return this.post(`modules/${enc(name)}/comments`, { text, parent }); }
  editComment(id, text) { return this.request("PATCH", `comments/${id}`, { json: { text } }); }
  deleteComment(id) { return this.request("DELETE", `comments/${id}`); }
  report(kind, target, reason, note) { return this.post("reports", { kind, target, reason, note }); }
  mine() { return this.get("me/modules"); }
  news() { return this.get("me/news"); }
  newsSeen() { return this.post("me/news/seen"); }
  profile(name) { return this.get(`members/${enc(name)}`); }
  devices() { return this.get("me/devices"); }
  signOutDevice(id) { return this.request("DELETE", `me/devices/${enc(id)}`); }
  deviceSession(body) { return this.post("devices/sessions", body); }
  deviceCode(body) { return this.post("devices/codes", body); }
  missing(hashes) { return this.post("blobs/missing", { hashes }).then((result) => result.missing); }
  putBlob(hash, bytes) { return this.request("PUT", `blobs/${hash}`, { body: bytes }); }
  reports(all = false) { return this.get(`moderation/reports${all ? "?all=1" : ""}`); }
  moderate(action, body) { return this.post(`moderation/${action}`, body); }
  signOut() { return this.post("auth/logout"); }

  get canUsePasskeys() { return typeof PublicKeyCredential === "function" && !!navigator.credentials; }

  // A passkey's answer to request options (sign-in, or a confirmation such
  // as publishing), in the JSON the server reads.
  async assertion(options) {
    const credential = await navigator.credentials.get({ publicKey: { ...options,
      challenge: b64url.decode(options.challenge),
      allowCredentials: (options.allowCredentials || []).map((item) => ({ ...item, id: b64url.decode(item.id) })) } });
    const response = credential.response;
    return {
      id: credential.id, type: credential.type,
      response: {
        clientDataJSON: b64url.encode(response.clientDataJSON),
        authenticatorData: b64url.encode(response.authenticatorData),
        signature: b64url.encode(response.signature),
        userHandle: response.userHandle ? b64url.encode(response.userHandle) : null,
      },
    };
  }

  async createPasskey(path, json) {
    const options = await this.post(`${path}/options`, json);
    const publicKey = {
      ...options,
      challenge: b64url.decode(options.challenge),
      user: { ...options.user, id: b64url.decode(options.user.id) },
      excludeCredentials: (options.excludeCredentials || []).map((item) => ({ ...item, id: b64url.decode(item.id) })),
    };
    const credential = await navigator.credentials.create({ publicKey });
    const response = credential.response;
    return this.post(`${path}/verify`, { credential: {
      id: credential.id, type: credential.type,
      response: {
        clientDataJSON: b64url.encode(response.clientDataJSON),
        attestationObject: b64url.encode(response.attestationObject),
        transports: response.getTransports ? response.getTransports() : [],
      },
    } });
  }

  async signIn() {
    const options = await this.post("auth/login/options");
    return this.post("auth/login/verify", { credential: await this.assertion({ ...options, allowCredentials: [] }) });
  }
  // join is a join code, which closed accounts need (server.mjs --registration).
  register(name, join = null) { return this.createPasskey("auth/register", { name, ...(join ? { join } : {}) }); }
  addPasskey() { return this.createPasskey("auth/passkeys", {}); }

  async publish(name, version, terms) {
    const options = await this.post(`modules/${enc(name)}/versions/${enc(version)}/publish/options`);
    return this.post(`modules/${enc(name)}/versions/${enc(version)}/publish`, { credential: await this.assertion(options), terms });
  }

  async deleteAccount() {
    const options = await this.post("me/delete/options");
    return this.post("me/delete", { credential: await this.assertion(options) });
  }
}
